import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/services/apiClient';

export const NO_PO = '__none__';
export const fmtQ = (q) => String(Math.round((Number(q) || 0) * 1000) / 1000);
export const isPcs = (unit) => ['pcs', 'pc', 'piece', 'pieces', 'nos', 'no'].includes((unit || 'pcs').trim().toLowerCase());
const slug = (s) => (s || '').replace(/[^A-Za-z0-9]+/g, '-');

/** 'PO/26-27/00042 line Raven: ordered 300, received 310, 5% limit 315; this bundle of 12 would make 322' — null when within the limit. */
export const overMsg = (po, line, pct, add, what = 'this bundle') => {
  const got = Number(line.received_qty) || 0;
  if (got + add <= (line.limit_qty ?? line.ordered_qty) + 1e-6) return null;
  return `${po.po_number} line ${line.supplier_design_name}: ordered ${fmtQ(line.ordered_qty)}, received ${fmtQ(got)}, ${fmtQ(pct)}% limit ${fmtQ(line.limit_qty)}; ${what} of ${fmtQ(add)} would make ${fmtQ(got + add)}`;
};

/** Received (+ pending) vs ordered bar — amber near the limit, red once the over-receipt limit would be crossed. */
export const LineProgress = ({ line, pending = 0, testId }) => {
  const got = (Number(line.received_qty) || 0) + (Number(pending) || 0);
  const lim = Number(line.limit_qty) || Number(line.ordered_qty) || 0;
  const tone = got > lim + 1e-6 ? 'bg-red-500' : got >= (Number(line.ordered_qty) || 0) * 0.95 ? 'bg-amber-500' : 'bg-emerald-500';
  const width = lim ? Math.min(100, (got / lim) * 100) : 0;
  const tick = lim ? Math.min(100, ((Number(line.ordered_qty) || 0) / lim) * 100) : 100;
  return (
    <div className="w-full" data-testid={testId}>
      <div className="relative h-2 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full ${tone} transition-[width] duration-300`} style={{ width: `${width}%` }} />
        <div className="absolute top-0 h-full w-px bg-gray-700/60" style={{ left: `${tick}%` }} />
      </div>
      <p className="text-[11px] text-gray-600 mt-0.5 tabular-nums">
        {fmtQ(line.received_qty)}{pending ? <span className="font-semibold text-blue-700"> + {fmtQ(pending)}</span> : null} / {fmtQ(line.ordered_qty)} {line.unit}
        <span className="text-gray-400"> · limit {fmtQ(lim)}</span>
      </p>
    </div>
  );
};

const LineRow = ({ line, selected, pending, onPick }) => (
  <button type="button" onClick={() => onPick(line)} data-testid={`po-line-${line.po_item_id}`}
    className={`w-full text-left rounded-md border px-2.5 py-2 transition-colors duration-150 ${selected ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600' : 'border-gray-200 bg-white hover:border-blue-300'}`}>
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="font-medium text-gray-900 truncate">{line.supplier_design_name}</span>
      <span className={`text-[11px] font-mono shrink-0 ${line.mapped_sort_no ? 'text-purple-700' : 'text-gray-400 italic'}`}>
        {line.mapped_sort_no || 'no sort no'}
      </span>
    </div>
    <LineProgress line={line} pending={selected ? pending : 0} />
  </button>
);

const Notice = ({ tone, children, testId }) => (
  <div data-testid={testId} className={`flex items-start gap-2 rounded-md border p-2 text-xs ${tone === 'red' ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{children}</span>
  </div>
);

/** PO + line picker for a supplier and section (open POs only). onChange({ po, line, pct, vendorOnPos }); po null = no PO. */
export const PoLinePicker = ({ section, vendorName, poId, lineId, onChange, pending = 0, refreshKey = 0, allowNone = true, showLines = true, testPrefix = 'po' }) => {
  const [data, setData] = useState(null);
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    let live = true;
    setData(null);
    if (!vendorName) return undefined;
    apiClient.get('/po-receipts/open-pos', { params: { vendor_name: vendorName, section } })
      .then((r) => live && setData(r.data)).catch(() => live && setData({ pos: [], vendor_on_pos: false, over_receipt_pct: 5 }));
    return () => { live = false; };
  }, [vendorName, section, refreshKey]);
  useEffect(() => {
    if (!data) return;
    const base = { pct: data.over_receipt_pct, vendorOnPos: data.vendor_on_pos };
    const po = data.pos.find((p) => p.po_id === poId) || (poId === NO_PO ? null : data.pos.length === 1 ? data.pos[0] : null);
    const line = po ? (po.lines.find((l) => l.po_item_id === lineId) || (po.lines.length === 1 ? po.lines[0] : null)) : null;
    cb.current({ ...base, po, line, none: poId === NO_PO });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!vendorName) return null;
  if (!data) return <p className="text-xs text-gray-500" data-testid={`${testPrefix}-loading`}>Loading Purchase Orders…</p>;
  const base = { pct: data.over_receipt_pct, vendorOnPos: data.vendor_on_pos };
  const po = data.pos.find((p) => p.po_id === poId) || null;
  if (!data.pos.length) {
    return data.vendor_on_pos
      ? <Notice tone="red" testId={`${testPrefix}-none-open`}>No open {section} Purchase Order for {vendorName}. Supplier deliveries can't be counted — ask Purchase to issue a PO. Returns / transfers can still be assigned.</Notice>
      : <Notice testId={`${testPrefix}-no-pos`}>{vendorName} has no {section} Purchase Orders — bundles are assigned without a PO.</Notice>;
  }
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-picker`}>
      <Select value={po ? po.po_id : (poId === NO_PO ? NO_PO : '')}
        onValueChange={(v) => { const p = data.pos.find((x) => x.po_id === v) || null; onChange({ ...base, po: p, line: p && p.lines.length === 1 ? p.lines[0] : null, none: v === NO_PO }); }}>
        <SelectTrigger className={`h-10 ${po ? '' : 'border-amber-400 bg-amber-50'}`} data-testid={`${testPrefix}-select`}>
          <SelectValue placeholder="Pick the Purchase Order" />
        </SelectTrigger>
        <SelectContent>
          {data.pos.map((p) => (
            <SelectItem key={p.po_id} value={p.po_id} data-testid={`${testPrefix}-opt-${slug(p.po_number)}`}>
              {p.po_number}{p.po_date ? ` · ${p.po_date}` : ''} · {p.lines.length} line{p.lines.length === 1 ? '' : 's'}
            </SelectItem>
          ))}
          {allowNone && <SelectItem value={NO_PO} data-testid={`${testPrefix}-opt-none`}>No PO — returns / transfers only</SelectItem>}
        </SelectContent>
      </Select>
      {po && showLines && (
        <div className="grid gap-1.5 max-h-64 overflow-y-auto pr-0.5">
          {po.lines.map((l) => <LineRow key={l.po_item_id} line={l} selected={l.po_item_id === lineId} pending={pending}
            onPick={(line) => onChange({ ...base, po, line, none: false })} />)}
        </div>
      )}
    </div>
  );
};
